import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { selectors, actions } from 'farmx-redux-core';
import { useLocation } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { ControlPumpDetailItem } from './ControlPumpDetailItem';
import { useTracking } from '../../../../helper/mixpanel';
import { sortArrayOfObjectsByProperty } from '../../../../helper/common';

const { loadSensorStatus: loadSensorStatusAction } = actions;

const REFRESH_INTERVAL = 1000 * 30;

export function ControlPumpDetails(props) {
  const {
    match, pumpId, loadStatus,
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const tracking = useTracking();
  const interval = useRef(null);

  const blockId = match.params ? Number(match.params.id) : pumpId;

  useEffect(() => {
    if (tracking) tracking.track('Loaded Pump Details', { pumpId: blockId });
  }, [blockId, tracking]);

  const pumpControllers = useSelector(
    (state) => selectors.selectBlockPumpControllers(state, blockId),
  );

  const pumpIdentifier = Object.keys(pumpControllers)?.[0];
  const pumpData = pumpControllers?.[pumpIdentifier];
  const pumpOverrideState = pumpData?.status?.overrideStatus?.state;
  const pumpControllersParams = JSON.stringify([
    { type: pumpData?.type, identifier: pumpData?.identifier }]);

  const flowMeter = useSelector(
    (state) => selectors.selectBlockFlowMeter(state, blockId),
  );

  const {
    identifier: flowMeterIdentifier,
    type: flowMeterType,
  } = flowMeter || {};

  const loadSensorsStatus = useCallback((sensorParams) => {
    sensorParams.forEach((sensorParam) => {
      dispatch(loadSensorStatusAction(sensorParam));
    });
  }, [dispatch]);

  useEffect(() => {
    const parsedSensorParams = JSON.parse(pumpControllersParams);
    loadSensorsStatus(parsedSensorParams);
    interval.current = setInterval(() => {
      loadSensorsStatus(parsedSensorParams);
    }, REFRESH_INTERVAL);
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [loadSensorsStatus, pumpControllersParams, pumpOverrideState]);

  useEffect(() => {
    if (flowMeterIdentifier && flowMeterType && loadStatus) {
      const flowMeterParams = { type: flowMeterType, identifier: flowMeterIdentifier };
      loadSensorsStatus([flowMeterParams]);
    }
  }, [dispatch, flowMeterType, flowMeterIdentifier, loadStatus, loadSensorsStatus]);

  const pumpDetails = Object.keys(pumpControllers).map((id) => pumpControllers[id]);

  const { state } = location;
  const identifier = state && state.identifier;
  const filteredPumpDetail = pumpDetails.filter((d) => d && d.identifier === identifier);
  const sortedPumpDetails = sortArrayOfObjectsByProperty(filteredPumpDetail);

  if (!pumpDetails[0]) {
    return (
      <div className="block-details-additional-info mobile-list-item list-control-pump-detail-item">
        {t('Pump details not available')}
      </div>
    );
  }

  const renderControlPumpDetailItem = (items = [], showPumpSpecs) => (
    items.map((pumpDetail, index) => (
      <ControlPumpDetailItem
        key={pumpDetail.identifier}
        pumpDetail={pumpDetail}
        loadStatus={loadStatus}
        showPumpSpecs={showPumpSpecs}
        showBlockName={items.length > 1}
        showHeader={!index}
      />
    ))
  );

  return (
    <>
      {state && state.goBack ? (
        <div className="div-default block-details-container">
          <PageHeader
            showBack
            title={`${t('Pump Detail')} - ${sortedPumpDetails[0].name
              || sortedPumpDetails[0].identifier}`}
          />
          {renderControlPumpDetailItem(sortedPumpDetails, true)}
        </div>
      ) : renderControlPumpDetailItem(pumpDetails, true)}
    </>
  );
}

ControlPumpDetails.defaultProps = {
  history: {},
  match: {},
  pumpId: null,
  loadStatus: true,
  status: null,
};

ControlPumpDetails.propTypes = {
  history: PropTypes.shape({
    goBack: PropTypes.func,
  }),
  match: PropTypes.shape({
    params: PropTypes.object,
  }),
  pumpId: PropTypes.number,
  loadStatus: PropTypes.bool,
  status: PropTypes.shape({}),
};
